import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Progress, Row, Segmented, Space, Spin, Statistic, Table, Tag, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { RenderingProxy } from '../services/catalogServiceAPI/renderingProxy';
import { duration, pct } from '../renderExperiments/shared';

const { Title, Text } = Typography;

const RANGE_OPTIONS = [
    { label: 'Last 24 hours', value: 'day' },
    { label: 'Last 7 days', value: 'week' },
    { label: 'Last 30 days', value: 'month' },
];

const PROVIDER_LABELS = { xai: 'Grok (xAI)', openai: 'ChatGPT (OpenAI)', mock: 'Mock' };
const ERROR_LABELS = {
    moderation: 'moderation',
    timeout: 'timeout',
    rate_limit: 'rate limit',
    server_error: '5xx',
    network: 'network',
    other: 'other',
};

const providerLabel = (id) => PROVIDER_LABELS[id] || id;

const rateColor = (value) => (value == null ? undefined : value >= 0.25 ? '#ff4d4f' : value >= 0.1 ? '#faad14' : '#52c41a');

const RateCell = ({ value }) =>
    value == null ? <Text type="secondary">—</Text> : (
        <Space size={8}>
            <Progress percent={Math.round(value * 100)} showInfo={false} size={[80, 8]} strokeColor={rateColor(value)} />
            <Text>{pct(value)}</Text>
        </Space>
    );

const formatBucket = (iso, bucket) => {
    const d = new Date(iso);
    return bucket === 'hour'
        ? d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
        : d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
};

const providerColumns = [
    { title: 'Provider', dataIndex: 'provider', render: (id, row) => <Space orientation="vertical" size={0}><Text strong>{providerLabel(id)}</Text><Text type="secondary" style={{ fontSize: 12 }}>{row.models.join(', ') || '—'}</Text></Space> },
    { title: 'Requests', dataIndex: 'requests', align: 'right' },
    { title: 'Succeeded', dataIndex: 'successes', align: 'right' },
    { title: 'Failed', dataIndex: 'failures', align: 'right' },
    { title: 'Fail rate', dataIndex: 'failRate', render: (v) => <RateCell value={v} /> },
    { title: 'Rejected', dataIndex: 'rejections', align: 'right' },
    { title: 'Rejection rate', dataIndex: 'rejectionRate', render: (v) => <RateCell value={v} /> },
    { title: 'Median call', dataIndex: ['duration', 'medianMs'], align: 'right', render: duration },
    { title: 'P90 call', dataIndex: ['duration', 'p90Ms'], align: 'right', render: duration },
    { title: 'Renders owned', dataIndex: 'renders', align: 'right', render: (v, row) => `${row.rendersCompleted} / ${v}` },
    {
        title: 'Errors',
        dataIndex: 'errors',
        render: (errors) => {
            const entries = Object.entries(errors || {});
            return entries.length ? entries.map(([kind, n]) => <Tag key={kind} color={kind === 'moderation' ? 'volcano' : 'default'}>{ERROR_LABELS[kind] || kind}: {n}</Tag>) : <Text type="secondary">—</Text>;
        },
    },
];

const bucketColumns = (report) => [
    { title: report.bucket === 'hour' ? 'Hour' : 'Day', dataIndex: 'start', render: (v) => formatBucket(v, report.bucket) },
    { title: 'Renders', dataIndex: 'renders', align: 'right' },
    { title: 'Completed', dataIndex: 'completed', align: 'right' },
    { title: 'Failed', dataIndex: 'failed', align: 'right' },
    { title: 'Fail rate', dataIndex: 'failRate', render: (v) => <RateCell value={v} /> },
    { title: 'Median time', dataIndex: 'medianDurationMs', align: 'right', render: duration },
    ...report.providers.map((p) => ({
        title: providerLabel(p.provider),
        children: [
            { title: 'Requests', key: `${p.provider}-requests`, align: 'right', render: (_, row) => row.providers[p.provider]?.requests ?? 0 },
            { title: 'Failed', key: `${p.provider}-failures`, align: 'right', render: (_, row) => row.providers[p.provider]?.failures ?? 0 },
            { title: 'Rejected', key: `${p.provider}-rejections`, align: 'right', render: (_, row) => row.providers[p.provider]?.rejections ?? 0 },
        ],
    })),
];

export const RenderPerformance = () => {
    const [range, setRange] = useState('week');
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setReport(await new RenderingProxy().performance(range));
        } catch (e) {
            setError(e.message || 'Failed to load render performance');
        } finally {
            setLoading(false);
        }
    }, [range]);

    useEffect(() => { load(); }, [load]);

    const totals = report?.totals;

    return (
        <div style={{ padding: 32 }}>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 24 }} align="start" wrap>
                <div>
                    <Title level={2} style={{ marginBottom: 4 }}>Render Performance</Title>
                    <Text type="secondary">Production renders and every provider call behind them, retries and fallbacks included.</Text>
                </div>
                <Space>
                    <Segmented options={RANGE_OPTIONS} value={range} onChange={setRange} />
                    <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
                </Space>
            </Space>

            {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 24 }} />}

            <Spin spinning={loading}>
                {report && (
                    <Space orientation="vertical" size={24} style={{ width: '100%' }}>
                        <Row gutter={[16, 16]}>
                            <Col xs={12} md={4}><Card><Statistic title="Renders" value={totals.renders} /></Card></Col>
                            <Col xs={12} md={4}><Card><Statistic title="Fail rate" value={pct(totals.failRate)} styles={{ content: { color: rateColor(totals.failRate) } }} /></Card></Col>
                            <Col xs={12} md={4}><Card><Statistic title="Failed / finished" value={`${totals.failed} / ${totals.completed + totals.failed}`} /></Card></Col>
                            <Col xs={12} md={4}><Card><Statistic title="Median render time" value={duration(totals.duration.medianMs)} /></Card></Col>
                            <Col xs={12} md={4}><Card><Statistic title="P90 render time" value={duration(totals.duration.p90Ms)} /></Card></Col>
                            <Col xs={12} md={4}><Card><Statistic title="In progress" value={totals.processing} /></Card></Col>
                        </Row>

                        <Card title="By provider" size="small">
                            <Table
                                dataSource={report.providers}
                                columns={providerColumns}
                                rowKey="provider"
                                size="small"
                                pagination={false}
                                scroll={{ x: true }}
                                locale={{ emptyText: 'No provider calls in this window' }}
                            />
                            <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                                Requests count individual provider calls. Rejected = the provider's content filter refused the image. Renders owned = renders this provider ended up responsible for, after any fallback (completed / total).
                            </Text>
                        </Card>

                        <Card title={report.bucket === 'hour' ? 'By hour' : 'By day'} size="small">
                            <Table
                                dataSource={report.buckets}
                                columns={bucketColumns(report)}
                                rowKey="start"
                                size="small"
                                pagination={false}
                                scroll={{ x: true }}
                                bordered
                            />
                        </Card>
                    </Space>
                )}
            </Spin>
        </div>
    );
};
